import React from 'react';
import './styles.css';

/**
 * 分頁組件
 * 用於顯示分頁控制
 * 
 * @param {Object} props - 組件屬性
 * @param {number} props.currentPage - 當前頁碼
 * @param {number} props.totalPages - 總頁數
 * @param {Function} props.onPageChange - 頁碼變更回調函數
 * @param {number} props.siblingCount - 當前頁碼兩側顯示的頁碼數量
 * @param {string} props.className - 額外的CSS類名
 * @returns {JSX.Element|null} 分頁組件
 */
function Pagination({
    currentPage,
    totalPages,
    onPageChange,
    siblingCount = 1,
    className = '',
}) {
    // 如果總頁數小於等於1，不顯示分頁
    if (totalPages <= 1) return null;

    // 生成頁碼數組
    const generatePageNumbers = () => {
        const pageNumbers = [];

        // 添加第一頁
        pageNumbers.push(1);

        // 計算左側範圍
        const leftSibling = Math.max(2, currentPage - siblingCount);

        // 如果左側範圍與第一頁不相鄰，添加省略號
        if (leftSibling > 2) {
            pageNumbers.push('...');
        }

        // 添加左側頁碼
        for (let i = leftSibling; i < currentPage; i++) {
            pageNumbers.push(i);
        }

        // 添加當前頁碼（如果不是第一頁或最後一頁）
        if (currentPage !== 1 && currentPage !== totalPages) {
            pageNumbers.push(currentPage);
        }

        // 計算右側範圍
        const rightSibling = Math.min(totalPages - 1, currentPage + siblingCount);

        // 添加右側頁碼
        for (let i = currentPage + 1; i <= rightSibling; i++) {
            pageNumbers.push(i);
        }

        // 如果右側範圍與最後一頁不相鄰，添加省略號
        if (rightSibling < totalPages - 1) {
            pageNumbers.push('...');
        }

        // 添加最後一頁（如果不是第一頁）
        if (totalPages !== 1) {
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    const pageNumbers = generatePageNumbers();

    return (
        <div className={`pagination ${className}`}>
            {/* 上一頁按鈕 */}
            <button
                className="pagination-button"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                上一頁
            </button>

            {/* 頁碼按鈕 */}
            <div className="pagination-numbers">
                {pageNumbers.map((pageNumber, index) => (
                    pageNumber === '...' ? (
                        <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                    ) : (
                        <button
                            key={pageNumber}
                            className={`pagination-number ${pageNumber === currentPage ? 'pagination-number-active' : ''}`}
                            onClick={() => onPageChange(pageNumber)}
                            disabled={pageNumber === currentPage}
                        >
                            {pageNumber}
                        </button>
                    )
                ))}
            </div>

            {/* 下一頁按鈕 */}
            <button
                className="pagination-button"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                下一頁
            </button>
        </div>
    );
}

export default Pagination; 